import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiUrl } from "../taskpane";
import { confirmDialog } from "../utils/confirmDialog";

const ACTION_STYLES = {
  LOGIN:                       { background: "#1e4a8a", label: "LOGIN" },
  CREATE_PROJECT:              { background: "#1a6e3a", label: "CREATE PROJECT" },
  CREATE_PROJECT_MEMBER:       { background: "#1f7a5a", label: "ADD MEMBER" },
  UPDATE_PROJECT_MEMBER:       { background: "#3c6f8a", label: "UPD MEMBER" },
  DELETE_PROJECT_MEMBER:       { background: "#8a3c5f", label: "REM MEMBER" },
  DELETE_PROJECT:              { background: "#8a1e1e", label: "DELETE PROJECT" },
  UPDATE_PROJECT:              { background: "#2a6a4a", label: "UPD PROJECT" },
  CATALOG_IMPORT:              { background: "#7a4a00", label: "CATALOG IMP" },
  PRICE_LIST_UPLOAD:           { background: "#4a1e8a", label: "PRICE UPLOAD" },
  CREATE_PRICE_LIST:           { background: "#1f5f3f", label: "CREATE PRICE LIST" },
  CREATE_CUSTOMER:             { background: "#0d5f6e", label: "CREATE CUSTOMER" },
  UPDATE_CUSTOMER:             { background: "#1a7a6a", label: "UPD CUSTOMER" },
  DELETE_CUSTOMER:             { background: "#7a1e4a", label: "DEL CUSTOMER" },
  CREATE_USER:                 { background: "#2e5a8a", label: "CREATE USER" },
  UPDATE_USER:                 { background: "#1a4a7a", label: "UPD USER" },
  DELETE_USER:                 { background: "#8a2e2e", label: "DEL USER" },
  CHANGE_PASSWORD:             { background: "#5a3a8a", label: "CHANGE PWD" },
  CREATE_BOQ_ROOT:             { background: "#1f6a8a", label: "BOQ ROOT" },
  CREATE_BOQ_VERSION:          { background: "#3a6a1a", label: "BOQ VERSION" },
  UPDATE_BOQ_VERSION:          { background: "#4a7a2a", label: "BOQ UPD" },
  DELETE_BOQ_VERSION:          { background: "#7a4a1a", label: "BOQ DEL" },
  CREATE_BOQ_CHILD_TABLE:      { background: "#2f7a9a", label: "BOQ CHILD" },
  RENAME_BOQ_BASE:             { background: "#3a5f7a", label: "BOQ RENAME" },
  REPLACE_PRODUCT_TABLE_ROWS:  { background: "#2a5a7a", label: "REPL ROWS" },
  REPLACE_PRODUCT_TABLE_ROWS_TIER1: { background: "#355b8a", label: "REPL TIER1" },
  CREATE_CATALOG:              { background: "#1a5a6a", label: "CREATE CAT" },
  DELETE_CATALOG:              { background: "#6a1a3a", label: "DEL CAT" },
  CATALOG_PREVIEW:             { background: "#6b5a1a", label: "CAT PREVIEW" },
  CATALOG_IMPORT_REVIEWED:     { background: "#5a6b1a", label: "CAT REVIEWED" },
  UPDATE_PRICE_LIST:           { background: "#236a52", label: "UPD PRICE LIST" },
  DELETE_PRICE_LIST:           { background: "#7a2f2f", label: "DEL PRICE LIST" },
  CREATE_PRICE_LIST_ITEM:      { background: "#2f6b5a", label: "CREATE PRICE" },
  UPDATE_PRICE_LIST_ITEM:      { background: "#3b6f7f", label: "UPD PRICE" },
  DELETE_PRICE_LIST_ITEM:      { background: "#7a3b3b", label: "DEL PRICE" },
  SAVE_TIER_CATALOG_SHEETS:    { background: "#2a6a7a", label: "SAVE SHEETS" },
  SAVE_TIER_CATALOG_MAIN_TABLE:{ background: "#2f5a6a", label: "SAVE TABLE" },
  CREATE_TIER_PRICE_LIST:      { background: "#1f7a6a", label: "TIER PRICE" },
};

const DEFAULT_STYLE = { background: "#445", label: null };

function ActionBadge({ action }) {
  const { background, label } = ACTION_STYLES[action] || DEFAULT_STYLE;
  return (
    <span style={{
      display: "inline-block", padding: "2px 7px", borderRadius: 4,
      fontSize: 10, fontWeight: 700, background, color: "#fff",
      letterSpacing: 0.4, whiteSpace: "nowrap", textAlign: "center",
    }}>
      {label || action}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ka-GE"); }
  catch { return iso; }
}

export default function LogPage({ token, onBack, isAdmin = false }) {
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [clearing, setClearing] = useState(false);
  const intervalRef = useRef(null);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");

  const actions = useMemo(() => Array.from(new Set(logs.map((l) => l.action).filter(Boolean))).sort(), [logs]);
  const users = useMemo(
    () => Array.from(new Set(logs.map((l) => l.user_name).filter(Boolean))).sort(),
    [logs]
  );
  const visibleLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter(
      (l) =>
        (actionFilter === "all" || l.action === actionFilter) &&
        (userFilter === "all" || l.user_name === userFilter) &&
        (!q || `${l.action} ${l.user_name || ""} ${l.detail || ""}`.toLowerCase().includes(q))
    );
  }, [logs, search, actionFilter, userFilter]);

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["time", "action", "user", "detail"], ...visibleLogs.map((l) => [l.created_at, l.action, l.user_name, l.detail])];
    const blob = new Blob(["\uFEFF" + rows.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch(apiUrl("activity-logs?limit=200"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err.message || "ჩატვირთვა ვერ მოხერხდა");
    }
  }, [token]);

  useEffect(() => {
    fetchLogs();
    intervalRef.current = setInterval(fetchLogs, 5000);
    return () => clearInterval(intervalRef.current);
  }, [fetchLogs]);

  const handleClearAll = useCallback(async () => {
    const ok = await confirmDialog(
      "ყველა ლოგის წაშლა?\n\nეს ოპერაცია შეუქცევადია — ყველა ჩანაწერი სამუდამოდ წაიშლება.",
      { confirmText: "წაშლა", cancelText: "გაუქმება", danger: true }
    );
    if (!ok) return;
    setClearing(true);
    clearInterval(intervalRef.current);
    try {
      const res = await fetch(apiUrl("activity-logs"), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLogs([]);
      setError(null);
    } catch (err) {
      setError(err.message || "წაშლა ვერ მოხერხდა");
    } finally {
      setClearing(false);
      intervalRef.current = setInterval(fetchLogs, 5000);
    }
  }, [token, fetchLogs]);

  return (
    <div style={{
      minHeight: "100vh", background: "var(--page-gradient)",
      padding: "20px 16px", color: "var(--text-primary)",
      fontFamily: "Arial, sans-serif",
    }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="link" onClick={onBack}>← უკან</button>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>აქტივობის ჟურნალი</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
              <span style={{
                display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                background: "#4caf50",
              }} />
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                პირდაპირი
                {lastRefresh && ` · განახლდა ${lastRefresh.toLocaleTimeString("ka-GE")}`}
              </span>
            </div>
          </div>
        </div>

        {isAdmin && (
          <button
            onClick={handleClearAll}
            disabled={clearing || logs.length === 0}
            style={{
              padding: "5px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer",
              background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
              color: clearing ? "var(--text-muted)" : "#f87171",
              opacity: logs.length === 0 ? 0.4 : 1,
            }}
          >
            {clearing ? "იშლება..." : "🗑 ყველა"}
          </button>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="ძიება..."
          style={{ flex: "1 1 140px", padding: "7px 10px", borderRadius: 8, fontSize: 12, border: "1px solid var(--field-border)", background: "var(--field-bg)", color: "var(--text-primary)" }}
        />
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
          style={{ padding: "7px 8px", borderRadius: 8, fontSize: 12, border: "1px solid var(--field-border)", background: "var(--field-bg)", color: "var(--text-primary)" }}>
          <option value="all">ყველა მოქმედება</option>
          {actions.map((a) => <option key={a} value={a}>{(ACTION_STYLES[a] || {}).label || a}</option>)}
        </select>
        <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
          style={{ padding: "7px 8px", borderRadius: 8, fontSize: 12, border: "1px solid var(--field-border)", background: "var(--field-bg)", color: "var(--text-primary)" }}>
          <option value="all">ყველა მომხმარებელი</option>
          {users.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <button onClick={exportCsv} disabled={!visibleLogs.length}
          style={{ padding: "7px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", border: "1px solid var(--surface-border)", background: "var(--surface-soft)", color: "var(--text-primary)" }}>
          ⬇ CSV
        </button>
        <span style={{ alignSelf: "center", fontSize: 11, color: "var(--text-muted)" }}>
          {visibleLogs.length} / {logs.length}
        </span>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: 8, padding: "10px 12px", marginBottom: 14,
          color: "#f87171", fontSize: 12,
        }}>
          შეცდომა: {error}
        </div>
      )}

      {/* ── Table ── */}
      <div style={{ overflowX: "auto" }}>
        <table style={{
          width: "100%", borderCollapse: "collapse",
          fontSize: 12, tableLayout: "fixed",
        }}>
          <thead>
            <tr>
              {[["23%","დრო"],["25%","მოქმედება"],["17%","მომხმარებელი"],["35%","დეტალი"]].map(([w,label]) => (
                <th key={label} style={{
                  textAlign: "left", padding: "7px 10px", width: w,
                  background: "var(--surface-soft)",
                  borderBottom: "1px solid rgba(148,163,184,0.2)",
                  color: "var(--text-muted)", fontWeight: 600, fontSize: 11,
                  textTransform: "uppercase", letterSpacing: 0.5,
                }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleLogs.length === 0 && !error && (
              <tr>
                <td colSpan={4} style={{
                  padding: "32px 0", textAlign: "center",
                  color: "var(--text-muted)", fontSize: 13,
                }}>
                  ჩანაწერი არ არის
                </td>
              </tr>
            )}
            {visibleLogs.map((log, i) => (
              <tr
                key={log.id}
                style={{ background: i % 2 === 1 ? "rgba(255,255,255,0.02)" : "transparent" }}
              >
                <td style={{
                  padding: "7px 10px",
                  borderBottom: "1px solid rgba(148,163,184,0.07)",
                  whiteSpace: "nowrap", color: "var(--text-muted)", fontSize: 11,
                }}>
                  {formatDate(log.created_at)}
                </td>
                <td style={{ padding: "7px 10px", borderBottom: "1px solid rgba(148,163,184,0.07)" }}>
                  <ActionBadge action={log.action} />
                </td>
                <td style={{
                  padding: "7px 10px",
                  borderBottom: "1px solid rgba(148,163,184,0.07)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {log.user_name || (log.user_id ? `#${log.user_id}` : "—")}
                </td>
                <td style={{
                  padding: "7px 10px",
                  borderBottom: "1px solid rgba(148,163,184,0.07)",
                  wordBreak: "break-word", overflowWrap: "anywhere",
                  color: "var(--text-muted)",
                }}>
                  {log.detail || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
